import type { Editor } from '@tiptap/core';
import type { EditorState as ProseMirrorEditorState } from '@tiptap/pm/state';
import type { IconType } from 'react-icons';
import type {} from '@tiptap/extension-list';

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import {
  FiBold,
  FiCheckSquare,
  FiClock,
  FiCode,
  FiFileText,
  FiGrid,
  FiImage,
  FiItalic,
  FiList,
  FiMapPin,
  FiMessageSquare,
  FiMinus,
  FiPaperclip,
  FiHash,
  FiType,
} from 'react-icons/fi';

import {
  createDefaultMarkdownCountdownEditValues,
  createDefaultMarkdownCountdownValue,
  type MarkdownCountdownEditRequest,
} from './markdown-countdown';
import { prependDocumentTitleTag } from './markdown-document-title';
import { createDefaultMarkdownFilesValue } from './markdown-files';
import { createDefaultMarkdownGalleryValue } from './markdown-gallery';
import {
  markdownImageEditValuesToAttrs,
  type MarkdownImageEditRequest,
} from './markdown-image-node';
import {
  createDefaultMarkdownMapEditValues,
  markdownMapEditValuesToBlockData,
  stringifyMarkdownMapConfig,
  type MarkdownMapEditRequest,
} from './markdown-map';
import {
  createDefaultMarkdownMessageEditValues,
  markdownMessageEditValuesToBlockData,
  stringifyMarkdownMessageConfig,
  type MarkdownMessageEditRequest,
} from './markdown-message';

export interface MarkdownSlashCommandRange {
  from: number;
  to: number;
}

export interface MarkdownSlashCommand {
  id: string;
  label: string;
  aliases: string[];
  icon: IconType;
  run: () => void;
}

export interface MarkdownSlashMenuState {
  active: boolean;
  query: string;
  range: MarkdownSlashCommandRange | null;
  selectedIndex: number;
}

interface MarkdownSlashTriggerMatch {
  from: number;
  query: string;
}

interface MarkdownSlashCommandDefinition {
  id: string;
  label: string;
  aliases: string[];
  icon: IconType;
  run: (
    editor: Editor,
    onRequestCountdownEdit?: (request: MarkdownCountdownEditRequest) => void,
    onRequestImageEdit?: (request: MarkdownImageEditRequest) => void,
    onRequestMapEdit?: (request: MarkdownMapEditRequest) => void,
    onRequestMessageEdit?: (request: MarkdownMessageEditRequest) => void,
  ) => void;
}

interface MarkdownSlashMenuOptions {
  onRequestCountdownEdit?: (request: MarkdownCountdownEditRequest) => void;
  onRequestImageEdit?: (request: MarkdownImageEditRequest) => void;
  onRequestMapEdit?: (request: MarkdownMapEditRequest) => void;
  onRequestMessageEdit?: (request: MarkdownMessageEditRequest) => void;
}

type MarkdownSlashMenuMeta =
  | { type: 'close' }
  | { type: 'setSelectedIndex'; index: number };

export const MARKDOWN_SLASH_MENU_PLUGIN_KEY =
  new PluginKey<MarkdownSlashMenuState>('markdownSlashMenu');

export const INACTIVE_MARKDOWN_SLASH_MENU_STATE: MarkdownSlashMenuState = {
  active: false,
  query: '',
  range: null,
  selectedIndex: 0,
};

function toggleHeading(editor: Editor, level: 1 | 2 | 3) {
  editor.chain().focus().toggleHeading({ level }).run();
}

function insertDocumentTitle(editor: Editor) {
  const current = editor.getMarkdown();
  const next = prependDocumentTitleTag(current);
  if (next === current) {
    editor.commands.focus('start');
    return;
  }

  editor.commands.setContent(next, {
    contentType: 'markdown',
    emitUpdate: true,
  });
}

function insertGallery(editor: Editor) {
  editor.commands.insertContent(createDefaultMarkdownGalleryValue(), {
    contentType: 'markdown',
  });
}

function insertFiles(editor: Editor) {
  editor.commands.insertContent(createDefaultMarkdownFilesValue(), {
    contentType: 'markdown',
  });
}

function insertCountdown(
  editor: Editor,
  onRequestCountdownEdit?: (request: MarkdownCountdownEditRequest) => void,
) {
  const insertAt = editor.state.selection.from;
  onRequestCountdownEdit?.({
    mode: 'insert',
    initialValues: createDefaultMarkdownCountdownEditValues(),
    onCommit: async (values) => {
      editor.commands.insertContentAt(
        insertAt,
        createDefaultMarkdownCountdownValue(values),
        {
          contentType: 'markdown',
        },
      );
    },
  });
}

function insertImage(
  editor: Editor,
  onRequestImageEdit?: (request: MarkdownImageEditRequest) => void,
) {
  const insertAt = editor.state.selection.from;
  onRequestImageEdit?.({
    mode: 'insert',
    initialValues: {
      src: '',
      width: '',
      height: '',
    },
    onCommit: async (values) => {
      const attrs = markdownImageEditValuesToAttrs(values);
      if (!attrs.src) {
        return;
      }

      editor
        .chain()
        .focus()
        .insertContentAt(insertAt, {
          type: 'image',
          attrs,
        })
        .run();
    },
  });
}

function insertMessage(
  editor: Editor,
  onRequestMessageEdit?: (request: MarkdownMessageEditRequest) => void,
) {
  const insertAt = editor.state.selection.from;
  onRequestMessageEdit?.({
    mode: 'insert',
    initialValues: createDefaultMarkdownMessageEditValues(),
    onCommit: async (values) => {
      const data = markdownMessageEditValuesToBlockData(values);

      editor
        .chain()
        .focus()
        .insertContentAt(insertAt, {
          type: 'npMessage',
          attrs: {
            data,
            rawConfig: stringifyMarkdownMessageConfig(data),
            parseError: null,
          },
        })
        .run();
    },
  });
}

function insertMap(
  editor: Editor,
  onRequestMapEdit?: (request: MarkdownMapEditRequest) => void,
) {
  onRequestMapEdit?.({
    mode: 'insert',
    initialValues: createDefaultMarkdownMapEditValues(),
    onCommit: async (values) => {
      const data = markdownMapEditValuesToBlockData(values);

      editor
        .chain()
        .focus()
        .insertContent({
          type: 'npMap',
          attrs: {
            data,
            rawConfig: stringifyMarkdownMapConfig(data),
            parseError: null,
          },
        })
        .run();
    },
  });
}

export function createMarkdownSlashCommands(
  editor: Editor,
  onRequestCountdownEdit?: (request: MarkdownCountdownEditRequest) => void,
  onRequestImageEdit?: (request: MarkdownImageEditRequest) => void,
  onRequestMapEdit?: (request: MarkdownMapEditRequest) => void,
  onRequestMessageEdit?: (request: MarkdownMessageEditRequest) => void,
): MarkdownSlashCommand[] {
  const commands: MarkdownSlashCommandDefinition[] = [
    {
      id: 'heading-1',
      label: 'Heading 1',
      aliases: ['h1', 'title', 'header 1'],
      icon: FiType,
      run: (currentEditor) => toggleHeading(currentEditor, 1),
    },
    {
      id: 'heading-2',
      label: 'Heading 2',
      aliases: ['h2', 'header 2'],
      icon: FiType,
      run: (currentEditor) => toggleHeading(currentEditor, 2),
    },
    {
      id: 'heading-3',
      label: 'Heading 3',
      aliases: ['h3', 'header 3'],
      icon: FiType,
      run: (currentEditor) => toggleHeading(currentEditor, 3),
    },
    {
      id: 'bold',
      label: 'Bold',
      aliases: ['strong'],
      icon: FiBold,
      run: (currentEditor) => currentEditor.chain().focus().toggleBold().run(),
    },
    {
      id: 'italic',
      label: 'Italic',
      aliases: ['emphasis', 'em'],
      icon: FiItalic,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleItalic().run(),
    },
    {
      id: 'strike',
      label: 'Strike',
      aliases: ['strikethrough', 'delete'],
      icon: FiMinus,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleStrike().run(),
    },
    {
      id: 'bullet-list',
      label: 'Bullet List',
      aliases: ['bullets', 'ul', 'unordered list'],
      icon: FiList,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleBulletList().run(),
    },
    {
      id: 'ordered-list',
      label: 'Ordered List',
      aliases: ['numbered list', 'ol'],
      icon: FiHash,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleOrderedList().run(),
    },
    {
      id: 'checklist',
      label: 'Checklist',
      aliases: ['task', 'todo', 'checkbox', 'task list'],
      icon: FiCheckSquare,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleTaskList().run(),
    },
    {
      id: 'blockquote',
      label: 'Quote',
      aliases: ['blockquote'],
      icon: FiMessageSquare,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleBlockquote().run(),
    },
    {
      id: 'code-block',
      label: 'Code',
      aliases: ['code block', 'pre'],
      icon: FiCode,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleCodeBlock().run(),
    },
    {
      id: 'table',
      label: 'Insert table',
      aliases: ['grid', 'spreadsheet'],
      icon: FiGrid,
      run: (currentEditor) =>
        currentEditor
          .chain()
          .focus()
          .insertTable({ rows: 4, cols: 3, withHeaderRow: true })
          .run(),
    },
    {
      id: 'document-title',
      label: 'Insert document title',
      aliases: ['document title', 'title block', 'np-document-title'],
      icon: FiFileText,
      run: insertDocumentTitle,
    },
    {
      id: 'gallery',
      label: 'Insert gallery',
      aliases: ['images', 'image grid', 'np-gallery'],
      icon: FiImage,
      run: insertGallery,
    },
    {
      id: 'countdown',
      label: 'Insert countdown',
      aliases: ['timer', 'deadline', 'np-countdown'],
      icon: FiClock,
      run: (currentEditor, requestCountdownEdit) =>
        insertCountdown(currentEditor, requestCountdownEdit),
    },
    {
      id: 'files',
      label: 'Insert files',
      aliases: ['attachments', 'uploads', 'np-files'],
      icon: FiPaperclip,
      run: insertFiles,
    },
    {
      id: 'image',
      label: 'Add image',
      aliases: ['img', 'insert image', 'picture'],
      icon: FiImage,
      run: (currentEditor, _requestCountdownEdit, requestImageEdit) =>
        insertImage(currentEditor, requestImageEdit),
    },
    {
      id: 'map',
      label: 'Insert map',
      aliases: ['google map', 'embed map', 'np-map'],
      icon: FiMapPin,
      run: (
        currentEditor,
        _requestCountdownEdit,
        _requestImageEdit,
        requestMapEdit,
      ) => insertMap(currentEditor, requestMapEdit),
    },
    {
      id: 'messages',
      label: 'Insert messages',
      aliases: ['chat', 'message block', 'np-message'],
      icon: FiMessageSquare,
      run: (
        currentEditor,
        _requestCountdownEdit,
        _requestImageEdit,
        _requestMapEdit,
        requestMessageEdit,
      ) => insertMessage(currentEditor, requestMessageEdit),
    },
  ];

  return commands.map((command) => ({
    ...command,
    run: () =>
      command.run(
        editor,
        onRequestCountdownEdit,
        onRequestImageEdit,
        onRequestMapEdit,
        onRequestMessageEdit,
      ),
  }));
}

export function filterMarkdownSlashCommands(
  commands: MarkdownSlashCommand[],
  query: string,
): MarkdownSlashCommand[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return commands;
  }

  return commands.filter((command) => {
    const haystacks = [command.label, ...command.aliases];
    return haystacks.some((value) =>
      value.toLowerCase().includes(normalizedQuery),
    );
  });
}

export function findMarkdownSlashTrigger(
  textBeforeCursor: string,
): MarkdownSlashTriggerMatch | null {
  const match = /(?:^|\s)\/([^\s/]*)$/.exec(textBeforeCursor);
  if (!match) {
    return null;
  }

  const slashOffset = match.index + match[0].lastIndexOf('/');
  return {
    from: slashOffset,
    query: match[1] ?? '',
  };
}

function isCodeContext(state: ProseMirrorEditorState): boolean {
  const marks = [
    ...state.selection.$from.marks(),
    ...(state.storedMarks ?? []),
  ];
  return (
    state.selection.$from.parent.type.spec.code === true ||
    marks.some((mark) => mark.type.spec.code === true)
  );
}

function getMarkdownSlashMenuMatch(
  state: ProseMirrorEditorState,
): { query: string; range: MarkdownSlashCommandRange } | null {
  if (!state.selection.empty) {
    return null;
  }

  const { $from, from } = state.selection;
  if (!$from.parent.isTextblock || isCodeContext(state)) {
    return null;
  }

  const textBeforeCursor = $from.parent.textBetween(
    0,
    $from.parentOffset,
    '\0',
    '\0',
  );
  const trigger = findMarkdownSlashTrigger(textBeforeCursor);
  if (!trigger) {
    return null;
  }

  return {
    query: trigger.query,
    range: {
      from: from - (textBeforeCursor.length - trigger.from),
      to: from,
    },
  };
}

function clampSelectedIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }

  return Math.min(Math.max(index, 0), length - 1);
}

export function closeMarkdownSlashMenu(editor: Editor) {
  editor.view.dispatch(
    editor.state.tr.setMeta(MARKDOWN_SLASH_MENU_PLUGIN_KEY, {
      type: 'close',
    } satisfies MarkdownSlashMenuMeta),
  );
}

export function setMarkdownSlashMenuSelectedIndex(
  editor: Editor,
  index: number,
) {
  editor.view.dispatch(
    editor.state.tr.setMeta(MARKDOWN_SLASH_MENU_PLUGIN_KEY, {
      type: 'setSelectedIndex',
      index,
    } satisfies MarkdownSlashMenuMeta),
  );
}

export function runMarkdownSlashCommand(
  editor: Editor,
  command: MarkdownSlashCommand,
  range: MarkdownSlashCommandRange,
) {
  editor.chain().focus().deleteRange(range).run();
  command.run();
}

export const MarkdownSlashMenuExtension = Extension.create({
  name: 'markdownSlashMenu',

  addOptions() {
    return {
      onRequestImageEdit: undefined,
      onRequestCountdownEdit: undefined,
      onRequestMapEdit: undefined,
      onRequestMessageEdit: undefined,
    } satisfies MarkdownSlashMenuOptions;
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const {
      onRequestCountdownEdit,
      onRequestImageEdit,
      onRequestMapEdit,
      onRequestMessageEdit,
    } = this.options as MarkdownSlashMenuOptions;

    return [
      new Plugin<MarkdownSlashMenuState>({
        key: MARKDOWN_SLASH_MENU_PLUGIN_KEY,
        state: {
          init: () => INACTIVE_MARKDOWN_SLASH_MENU_STATE,
          apply: (transaction, pluginState, _oldState, newState) => {
            const meta = transaction.getMeta(MARKDOWN_SLASH_MENU_PLUGIN_KEY) as
              | MarkdownSlashMenuMeta
              | undefined;

            if (meta?.type === 'close') {
              return INACTIVE_MARKDOWN_SLASH_MENU_STATE;
            }

            const match = getMarkdownSlashMenuMatch(newState);
            if (!match) {
              return INACTIVE_MARKDOWN_SLASH_MENU_STATE;
            }

            const commands = filterMarkdownSlashCommands(
              createMarkdownSlashCommands(
                editor,
                onRequestCountdownEdit,
                onRequestImageEdit,
                onRequestMapEdit,
                onRequestMessageEdit,
              ),
              match.query,
            );
            if (commands.length === 0) {
              return INACTIVE_MARKDOWN_SLASH_MENU_STATE;
            }

            const requestedIndex =
              meta?.type === 'setSelectedIndex'
                ? meta.index
                : pluginState.selectedIndex;
            const selectedIndex =
              pluginState.active &&
              pluginState.query === match.query &&
              pluginState.range?.from === match.range.from
                ? clampSelectedIndex(requestedIndex, commands.length)
                : 0;

            return {
              active: true,
              query: match.query,
              range: match.range,
              selectedIndex,
            };
          },
        },
        props: {
          handleKeyDown(view, event) {
            const menuState =
              MARKDOWN_SLASH_MENU_PLUGIN_KEY.getState(view.state) ??
              INACTIVE_MARKDOWN_SLASH_MENU_STATE;
            if (!menuState.active || !menuState.range) {
              return false;
            }

            const commands = filterMarkdownSlashCommands(
              createMarkdownSlashCommands(
                editor,
                onRequestCountdownEdit,
                onRequestImageEdit,
                onRequestMapEdit,
                onRequestMessageEdit,
              ),
              menuState.query,
            );
            if (commands.length === 0) {
              return false;
            }

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setMarkdownSlashMenuSelectedIndex(
                editor,
                (menuState.selectedIndex + 1) % commands.length,
              );
              return true;
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setMarkdownSlashMenuSelectedIndex(
                editor,
                (menuState.selectedIndex - 1 + commands.length) %
                  commands.length,
              );
              return true;
            }

            if (event.key === 'Enter') {
              event.preventDefault();
              runMarkdownSlashCommand(
                editor,
                commands[menuState.selectedIndex] ?? commands[0],
                menuState.range,
              );
              return true;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              closeMarkdownSlashMenu(editor);
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
